/* Copyright (c) 2023 Xperi. All Rights Reserved.  */

import { AnalyticsConstants, Diagnostics, Payload, VU } from '@tivocorp/tivo-webapp-loggers';
import { Error404DBDeleteCodeEnum } from 'client-code/device-binding-service';

import { DeviceModel } from '../../../core/device/DeviceModel';
import { BrandingUiInfoKeys } from '../../../core/entities/BrandingUiInfoKeys';
import { ErrorTypes } from '../../../core/error';
import { SignOutError } from '../../../core/error/SignOutError';
import { AuthServicesClientImpl } from '../../../core/impls/AuthServicesClientImpl';
import { SlsServiceGlobalEndpointsService } from '../../../core/infrastructure/SlsServiceGlobalEndpointsService';
import { ServicesClientImpl } from '../../../core/interactors/ServicesClientImpl';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { OperationsLogger, RestartReasonsOperationsLogger, VuLogger } from '../../../core/logging';
import { Log } from '../../../core/logging/Log';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { Storage } from '../../../core/storage/Storage';
import { BrandingUiUtility } from '../../../core/utils/BrandingUiUtility';
import { OverlayTypes } from '../../overlay/OverlayTypes';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { hideSpinner, showSpinner } from '../../spinner/spinner.reducers';

const TAG: string = 'AccountsOverlays';

const GLOBAL_MOUSE_TRAP: string = 'HideAccountsOverlay';

/**
 * List of helper functions.
 */

/**
 * Performs necessary cleanup tasks after a user signs out. Optionally, the app can be forcibly
 * closed.
 * @param forceClose (boolean): - `true` to close app after cleanup.
 */
const doPostSignOutOperations = (forceClose: boolean) => {
    Diagnostics.info(TAG, 'Signout successful.');
    RestartReasonsOperationsLogger.getInstance().updateAppState(
        RestartReasonsOperationsLogger.AppState.NORMAL_USER_EXIT,
    );
    Storage.clearAll([Storage.Keys.CA_DEVICE_ID, Storage.Keys.DEVICE_UUID, Storage.Keys.APP_STATE]);
    if (forceClose) DeviceModel.closeApplication();
};

/**
 * Below are the dispatchables.
 */
export const onMouseClickAccountsOverlay = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;
    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }
    dispatch(onKeyOkAccountsOverlay());
};

export const showResetAppOverlay = () => (dispatch: Function) => {
    const services = new ServicesClientImpl();
    const displayName = services.getBrandingUiInfoForKey(BrandingUiInfoKeys.DISPLAY_NAME);
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.SETTINGS_RESET_APP_OVERLAY_TASKS));
    const overlayDialogProps: IOverlayDialog = {
        description: Locale.getString(LocaleKeys.SETTINGS_RESET_TO_DEFAULT_TEXT).replace(
            'DISPLAY_NAME',
            displayName,
        ),
        hasDimmer: true,
        hasSeperator: false,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: [
            { text: `${Locale.getString(LocaleKeys.SETTINGS_RESET_TEXT)} ${displayName}` },
            { text: Locale.getString(LocaleKeys.OVERLAY_OPTION_CANCEL_TEXT) },
        ],
        overlayType: OverlayTypes.RESET_APP,
        title: Locale.getString(LocaleKeys.SETTINGS_CONFIRM_APP_RESET),
    };
    dispatch(showOverlayDialog(overlayDialogProps));
};

export const showSignOutOverlay = () => (dispatch: Function) => {
    const services = new ServicesClientImpl();
    const displayName = services.getBrandingUiInfoForKey(BrandingUiInfoKeys.DISPLAY_NAME);
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.SETTINGS_SIGN_OUT_OVERLAY_TASKS));
    const overlayDialogProps: IOverlayDialog = {
        description: `${Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT_OF).replace(
            'DISPLAY_NAME',
            displayName,
        )}`,
        hasDimmer: true,
        hasSeperator: false,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: [
            { text: Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT_APP_EXIT_TEXT) },
            { text: Locale.getString(LocaleKeys.OVERLAY_OPTION_CANCEL_TEXT) },
        ],
        overlayType: OverlayTypes.SIGN_OUT,
        title: Locale.getString(LocaleKeys.SETTINGS_CONFIRM_APP_SIGN_OUT),
    };
    dispatch(showOverlayDialog(overlayDialogProps));
};

export const showSignOutErrorOverlay = (error?: SignOutError | any) => (dispatch: Function) => {
    dispatch(hideOverlayDialog());
    dispatch(
        setScreenTaskController(ScreenTaskControllerTypes.SETTINGS_SIGN_OUT_ERROR_OVERLAY_TASKS),
    );
    const overlayDialogProps: IOverlayDialog = {
        description: error.description || error?.getDescription(),
        hasDimmer: true,
        hasSeperator: false,
        messageCode: error.messageCode || error?.getCode(),
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: [{ text: Locale.getString(LocaleKeys.OVERLAY_OPTION_OK_TEXT) }],
        overlayType: OverlayTypes.SIGN_OUT_ERROR,
        title: error.title || error?.getTitle(),
    };
    dispatch(showOverlayDialog(overlayDialogProps));
};

export const signOutOverlayOperations = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;
    if (overlayHighlightVerticalPosition === 0) {
        dispatch(signOut(true));
    } else dispatch(hideOverlayDialog());
};

export const signOut =
    (forceClose = false) =>
    async (dispatch: Function) => {
        if (NetworkManager.isNetworkDisconnected()) {
            const services = new ServicesClientImpl();
            const displayName = services.getBrandingUiInfoForKey(BrandingUiInfoKeys.DISPLAY_NAME);
            const error = {
                title: Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT_ERROR),
                description: `${Locale.getString(
                    LocaleKeys.SETTINGS_SIGN_OUT_NETWORK_ERROR_TEXT,
                )} ${displayName}${Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT_ERROR_TEXT)}`,
                messageCode: ErrorTypes.NO_NETWORK_OVERLAY_ERROR_CODE,
            };
            dispatch(showSignOutErrorOverlay(error));
        } else {
            try {
                Diagnostics.warn(TAG, 'Performing sign out.');
                dispatch(
                    showSpinner(Locale.getString(LocaleKeys.SETTINGS_SIGN_OUT_SPINNER_MESSAGE)),
                );
                const authServices = new AuthServicesClientImpl({
                    iptvUrl: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                        SlsServiceGlobalEndpointsService.IPTV_LOGIN_SERVICE,
                    ),
                    deviceBinding: SlsServiceGlobalEndpointsService.getServiceEndpointByServiceName(
                        SlsServiceGlobalEndpointsService.DEVICE_BINDING_SERVICE,
                    ),
                });
                const payload: Payload = new Payload();
                payload.add(
                    AnalyticsConstants.Fields.SCREEN_NAME,
                    AnalyticsConstants.Screens.SETTINGS_SCREEN,
                );
                VU.logoutEvent(payload);
                VuLogger.logAppExitEvent(VuLogger.AppExitActions.EXIT);
                await authServices.removeDeviceBinding();
                doPostSignOutOperations(forceClose);
            } catch (error: any) {
                Diagnostics.error(TAG, 'Failed to sign out.');
                const defaultServices = new ServicesClientImpl();
                const errorDescription = Locale.getString(
                    LocaleKeys.SETTINGS_SIGN_OUT_SERVICE_ERROR_TEXT,
                );
                const replaceValues = {
                    PHONE_CUSTOMER_SUPPORT_NUMBER: defaultServices.getBrandingUiInfoForKey(
                        BrandingUiInfoKeys.PHONE_CUSTOMER_SUPPORT_NUMBER,
                    ),
                    CUSTOMER_SUPPORT_NAME: defaultServices.getBrandingUiInfoForKey(
                        BrandingUiInfoKeys.CUSTOMER_SUPPORT_NAME,
                    ),
                };
                const signOutError = error?.response?.data;
                const settingSignOutError = new SignOutError(
                    signOutError?.message,
                    signOutError?.code,
                    BrandingUiUtility.formatDescription(errorDescription, replaceValues),
                );
                VuLogger.logAppError(settingSignOutError);
                OperationsLogger.logUIErrorCode(settingSignOutError.getCode());
                if (
                    signOutError?.code === Error404DBDeleteCodeEnum.DbsDeviceBindingNotFoundToRemove
                ) {
                    Diagnostics.info(TAG, 'No Device Binding found at DBS');
                    doPostSignOutOperations(forceClose);
                } else {
                    dispatch(showSignOutErrorOverlay(settingSignOutError));
                    dispatch(hideSpinner());
                }
            }
        }
    };

export const resetAppOverlayOperations = () => (dispatch: Function, getState: Function) => {
    const { verticalPosition: overlayHighlightVerticalPosition } = getState().overlayHighlight;
    if (overlayHighlightVerticalPosition === 0) {
        // TODO: We need to see how we can log this event after the app reload.
        Diagnostics.info(TAG, 'User has cleared the app data');
        RestartReasonsOperationsLogger.getInstance().updateAppState(
            RestartReasonsOperationsLogger.AppState.NORMAL_USER_EXIT,
        );
        // Specify the list of storage keys whose data needs to be retained after an app reset.
        Storage.clearAll([
            Storage.Keys.CA_DEVICE_ID,
            Storage.Keys.DEVICE_UUID,
            Storage.Keys.APP_STATE,
            Storage.Keys.LICENSE_PLATE_AUTH_CODE,
        ]);
        DeviceModel.restartApplication();
    } else dispatch(hideOverlayDialog());
};

export const onKeyOkAccountsOverlay = () => (dispatch: Function, getState: Function) => {
    const { screenTaskController } = getState().screens;
    Log.i(TAG, 'OK has been pressed');

    switch (screenTaskController) {
        case ScreenTaskControllerTypes.SETTINGS_RESET_APP_OVERLAY_TASKS:
            dispatch(resetAppOverlayOperations());
            break;

        case ScreenTaskControllerTypes.SETTINGS_SIGN_OUT_OVERLAY_TASKS:
            dispatch(signOutOverlayOperations());
            break;

        case ScreenTaskControllerTypes.SETTINGS_SIGN_OUT_ERROR_OVERLAY_TASKS:
            dispatch(hideOverlayDialog());
            break;

        default:
    }
};
